import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API_URL } from "General/APIEndpoints";
import { ObjectDataStore } from "Stores/RESTStoreInstances/ObjectDataStore";
import { API } from "General/API";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { download } from "utils/download";

class GeneralFormStore extends ObjectDataStore<API_GeneralSettings> {
  constructor() {
    super();
    makeObservable(this, {
      put: action.bound,
    });

    this.data = {} as API_GeneralSettings;
  }
  url() {
    return API_URL.PROJECTS.getGeneralProjectSettings(projectsDataStore.projectId);
  }

  @handleStoreState()
  async put(formData: API_PrintJobSettings) {
    try {
      const data = { ...this.data, ...formData };
      await API.editData(this.url(), data);
      runInAction(() => {
        this.data = data;
      });
      await projectsDataStore.getCurrentProject(true);
    } catch (err) {
      throw err;
    }
  }
}

class InfoMailsFormStore extends ObjectDataStore<API_InfoMailSettings> {
  constructor() {
    super();
    makeObservable(this, {
      put: action.bound,
    });
    this.data = {} as API_InfoMailSettings;
  }
  url() {
    return API_URL.PROJECTS.getMailSettings(projectsDataStore.projectId);
  }

  @handleStoreState()
  async put(formData: API_InfoMailSettings) {
    try {
      const data = { ...this.data, ...formData };
      await API.editData(this.url(), data);
      runInAction(() => {
        this.data = data;
      });
      await projectsDataStore.getCurrentProject(true);
    } catch (err) {
      throw err;
    }
  }
}

class PlankeyFormStore extends CoreStore {
  initialFileData: API_FilenameConventionFileData = {};
  isDownloading = false;

  constructor() {
    super();

    makeObservable(this, {
      initialFileData: observable,
      isDownloading: observable,
      clear: action.bound,
      downloadFile: action.bound,
      file: computed,
      toggleDownloading: action.bound,
      fetch: action.bound,
      edit: action.bound,
    });
  }

  url() {
    return API_URL.PROJECTS.getFilenameConventionFileData(projectsDataStore.projectId);
  }

  clear() {
    this.initialFileData = {};
    this.isDownloading = false;
  }

  @handleStoreState()
  async fetch() {
    try {
      const data = await API.getData(this.url());
      runInAction(() => {
        this.initialFileData = data;
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async edit(id: number) {
    try {
      await API.editData<API_FilenameConventionFileData>(this.url(), {
        id,
      });
      projectsDataStore.getCurrentProject(true);
      await this.fetch();
    } catch (err) {
      throw err;
    }
  }

  async downloadFile(id?: number) {
    this.toggleDownloading();
    try {
      const formBody: API_DownloadWizardProperties = {
        files: [{ id: id || this.file.id, state: "ACTIVE" }],
      };
      const file: File = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formBody
      );
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    } finally {
      this.toggleDownloading();
    }
  }

  get file() {
    return this.initialFileData;
  }

  toggleDownloading() {
    this.isDownloading = !this.isDownloading;
  }
}

class ZipUploadFormStore extends CoreStore {
  data: boolean = null;

  constructor() {
    super();
    makeObservable(this, {
      data: observable,
      put: action.bound,
      fetch: action.bound,
      clear: action,
      setCheckData: action.bound,
    });
  }

  url() {
    return API_URL.PROJECTS.getZipFileProjectSettings(projectsDataStore.projectId);
  }

  clear() {
    this.data = null;
  }

  @handleStoreState()
  async put() {
    try {
      const body: API_ZipFileSettings = { zipUpload: this.data };
      await API.editData(this.url(), body);
      await projectsDataStore.getCurrentProject(true);
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async fetch() {
    try {
      const response = await API.getData<API_ZipFileSettings>(this.url());
      runInAction(() => {
        if (response) {
          this.data = response.zipUpload;
        }
      });
    } catch (err) {
      throw err;
    }
  }

  setCheckData(_v?: boolean) {
    this.data = !this.data;
  }
}

export const infoMailsFormStore = new InfoMailsFormStore();
export const plankeyFormStore = new PlankeyFormStore();
export const zipUploadFormStore = new ZipUploadFormStore();
export const generalFormStore = new GeneralFormStore();
